import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BankLensProvider, useBankLens } from './BankLensContext';
import BankLensOverview from './BankLensOverview';
import BankLensDetail from './BankLensDetail';
import BankLensCompare from './BankLensCompare';
import styles from './BankLensPage.module.css';

function BankLensInner() {
    const navigate = useNavigate();
    const { banks, loading, error } = useBankLens();
    const [view, setView] = useState('overview');       // 'overview' | 'compare' | 'detail'
    const [selectedBank, setSelectedBank] = useState(null); // ticker string

    const goOverview = useCallback(() => { setView('overview'); setSelectedBank(null); }, []);
    const goCompare = useCallback(() => { setView('compare'); setSelectedBank(null); }, []);
    const goDetail = useCallback((ticker) => { setView('detail'); setSelectedBank(ticker); }, []);

    return (
        <div className={styles.page}>
            {/* ── Sidebar ──────────────────────────────── */}
            <aside className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <button
                        type="button"
                        className={styles.navItem}
                        onClick={() => navigate('/dashboards')}
                        style={{ marginBottom: 12 }}
                    >
                        ← Dashboards
                    </button>
                    <div className={styles.logoRow}>
                        <div className={styles.logoIcon}>BL</div>
                        <div>
                            <div className={styles.logoText}>BankLens</div>
                            <div className={styles.logoSub}>India Banking Dashboard</div>
                        </div>
                    </div>
                </div>

                <nav className={styles.navSection}>
                    <button
                        className={view === 'overview' ? styles.navItemActive : styles.navItem}
                        onClick={goOverview}
                    >
                        <span className={styles.navIcon}>📊</span>Overview
                    </button>
                    <button
                        className={view === 'compare' ? styles.navItemActive : styles.navItem}
                        onClick={goCompare}
                    >
                        <span className={styles.navIcon}>⚖️</span>Compare
                    </button>
                </nav>

                <div className={styles.bankListSection}>
                    <div className={styles.bankListLabel}>Banks</div>
                    {banks.map(b => (
                        <button
                            key={b.ticker}
                            className={
                                view === 'detail' && selectedBank === b.ticker
                                    ? styles.bankItemActive
                                    : styles.bankItem
                            }
                            onClick={() => goDetail(b.ticker)}
                        >
                            <span className={styles.bankDot} style={{ background: b.color }} />
                            {b.shortName}
                            <span className={`${styles.bankTypeBadge} ${b.type === 'PVT' ? styles.badgePVT : styles.badgePSU}`}>
                                {b.type}
                            </span>
                        </button>
                    ))}
                </div>

                <div className={styles.sidebarFooter}>
                    {loading ? 'Loading data…' : error ? 'Using cached data' : 'Live data'}
                </div>
            </aside>

            {/* ── Main content ─────────────────────────── */}
            <main className={styles.main}>
                {view === 'overview' && (
                    <BankLensOverview onSelectBank={goDetail} />
                )}
                {view === 'detail' && selectedBank && (
                    <BankLensDetail ticker={selectedBank} onBack={goOverview} />
                )}
                {view === 'compare' && (
                    <BankLensCompare onSelectBank={goDetail} />
                )}
            </main>
        </div>
    );
}

export default function BankLensPage() {
    return (
        <BankLensProvider>
            <BankLensInner />
        </BankLensProvider>
    );
}
